import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { TextStyle, View, ViewStyle, ActivityIndicator } from 'react-native'
import { useHistory } from 'react-router'
import { useStores, setPageTitle } from '@trivie/core'
import {
  Avatar,
  Button,
  color,
  Header,
  iconCreate,
  Post,
  spacing,
  SvgIcon,
  Text,
} from '@trivie/ui-mobile'
import { ReactComponent as TalkNull } from './talk-null.svg'
import { CreatePostDialog } from './create-post-dialog'
import InfiniteScroll from 'react-infinite-scroll-component'
import { CircularProgress } from '@mui/material'
import { SubscriptionLockedScreen } from '@trivie/ui-mobile/components/subscription-locked-screen'
export interface HistoryProps {
  topicId?: number | undefined
}

export const TalkScreen = observer(() => {
  const { libraryStore, subscriptionStore, userStore, talkStore, formStore } = useStores()
  const {
    sortedPosts,
    likePost,
    deletePost,
    refreshPosts,
    setSelectedPost,
    currentPage,
    getPosts,
    lastPage,
    isLoading,
  } = talkStore

  const { getTopicsForUser } = libraryStore

  const { user, getTalkPermission, getPermissions } = userStore
  const { setFormData } = formStore
  const [showPostDialog, setShowPostDialog] = useState(false)
  const [canPost, setCanPost] = useState(false)
  const [screenHeight, setScreenHeight] = useState(0)
  const [isShowPost, setIsShowPost] = useState(false)

  const history = useHistory()

  useEffect(() => {
    const content = document.getElementById('rootContent')
    if (content) {
      content.style.overflow = 'hidden'
    }

    const height = window.innerHeight
    setScreenHeight(height)

    if (user) {
      getPermissions(user.id)
      setCanPost(getTalkPermission('can_create_post') || getTalkPermission('can_edit_post'))
      getTopicsForUser(user.id)
      refreshPosts()
    }
    setPageTitle(`Feed - ${process.env.REACT_APP_SITE_TITLE}`)
  }, [])

  useEffect(() => {
    setIsShowPost(!userStore?.permissions?.restrict_talk || getTalkPermission('can_edit_post'))
  }, [userStore?.permissions])

  const onComment = (post: any) => {
    setSelectedPost(post)
    if (post.resources) {
      talkStore.setSelectedPostPhoto(post.resources)
    }
    history.push(`/talk/${post.id}`)
  }

  const onLike = (postId: number) => {
    likePost(postId)
  }

  const onDelete = (postId: number) => {
    deletePost(postId).then(() => {
      refreshPosts()
    })
  }

  const [loading, setLoading] = useState(false)
  const renderItem = ({ item, index }: { item: any; index: number }) => {
    const photos = [] as any
    let video
    if (item.resources && item.resources.length > 0) {
      item.resources.forEach((resource: any) => {
        if (
          ['jpg', 'webp', 'jpeg', 'png', 'gif'].includes(
            (resource.extension ?? '').toLocaleLowerCase(),
          )
        ) {
          photos.push(resource)
        } else {
          video = resource
        }
      })
    }

    return photos && photos.length > 0 ? (
      <Post
        type="photo"
        title={`${item.author.first_name} ${item.author.last_name}`}
        subtitle={item.title}
        avatar={item.author.photo_url}
        post={item}
        onComment={() => onComment(item)}
        onLike={() => onLike(item.id)}
        onDelete={() => onDelete(item.id)}
        onTogglePin={() => {
          setLoading(true)
          refreshPosts().then(() => setLoading(false))
        }}
        index={index}
        photos={photos}
      />
    ) : video ? (
      <Post
        type="video"
        title={`${item.author.first_name} ${item.author.last_name}`}
        subtitle={item.title}
        avatar={item.author.photo_url}
        post={item}
        onComment={() => onComment(item)}
        onLike={() => onLike(item.id)}
        onDelete={() => onDelete(item.id)}
        onTogglePin={() => {
          setLoading(true)
          refreshPosts().then(() => setLoading(false))
        }}
        index={index}
        video={video}
      />
    ) : (
      <Post
        type="text"
        title={`${item.author.first_name} ${item.author.last_name}`}
        subtitle={item.title}
        avatar={item.author.photo_url}
        post={item}
        onComment={() => onComment(item)}
        onLike={() => onLike(item.id)}
        onDelete={() => onDelete(item.id)}
        onTogglePin={() => {
          setLoading(true)
          refreshPosts().then(() => setLoading(false))
        }}
        index={index}
      />
    )
  }

  const [fetched, setFetched] = useState(1)
  const nextPage = () => {
    const next = currentPage + 1
    if (talkStore.lastPage && talkStore.currentPage < talkStore.lastPage && fetched < next) {
      setFetched(next)
      getPosts(next)
    }
  }
  const params = history.location.state as HistoryProps

  const hasActive = subscriptionStore.hasActiveSubscriptions

  if (userStore.isSubscriptionWorkspace && !hasActive) {
    return (
      <View testID="talk-screen" style={FULL}>
        <div
          style={{
            ...CONTAINER,
            flexDirection: 'column',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          <div style={HEADER_STICKY_CONTAINER}>
            <Header titleStyle={HEADER} headerTx="Discuss.header" />
          </div>
          <SubscriptionLockedScreen
            emoji="📚"
            title="No active subscriptions"
            helperText="Purchase or renew a subscription to start learning"
            buttonText="Browse Products"
            onButtonPress={
              userStore.externalStoreUrl
                ? () => {
                    window.open(
                      // @ts-ignore
                      userStore.externalStoreUrl,
                      '_blank',
                      'noopener',
                    )
                  }
                : undefined
            }
          />
        </div>
      </View>
    )
  }

  return (
    <>
      <View testID="talk-screen" style={FULL}>
        <div style={CONTAINER}>
          <div style={HEADER_STICKY_CONTAINER}>
            <Header titleStyle={HEADER} headerTx="Discuss.header" />
            <View
              accessibilityRole="image"
              accessibilityLabel={`${user?.first_name} ${user?.last_name} profile picture`}
              style={SUBHEADER}
            >
              <div onClick={() => history.push('/profile/stats')}>
                <Avatar
                  style={{ marginRight: 20 }}
                  size="small"
                  source={user?.photo_url}
                  name={`${user?.first_name} ${user?.last_name}`}
                />
              </div>
              {isShowPost && canPost && (
                <Button
                  // @ts-ignore
                  dataSet={{ qat: 'create_post_btn' }}
                  accessibilityRole="button"
                  accessibilityLabel="Open create post pop up"
                  style={{ flex: 1, backgroundColor: '#EBEBEB', height: 36, borderRadius: 8 }}
                  onPress={() => {
                    setFormData({})
                    formStore.setVideo(false)
                    setShowPostDialog(true)
                  }}
                >
                  <View style={{ flexDirection: 'row', alignSelf: 'center', alignItems: 'center' }}>
                    <SvgIcon color="#000" viewBox="0 0 24 24" src={iconCreate} size={16} />
                    <Text
                      style={{ color: color.black, paddingLeft: spacing[2], fontWeight: '600' }}
                      tx="Discuss.create"
                      variant="body1Emphasized"
                    />
                  </View>
                </Button>
              )}
            </View>
          </div>
          {isLoading ? (
            <div style={PROGRESS}>
              <CircularProgress />
            </div>
          ) : sortedPosts.length ? (
            <InfiniteScroll
              dataLength={sortedPosts.length} //This is important field to render the next data
              next={nextPage}
              height={screenHeight - 208}
              hasMore={
                lastPage && currentPage < lastPage && fetched < currentPage + 1 ? true : false
              }
              loader={
                <div style={LOADER}>
                  <ActivityIndicator size="small" />
                </div>
              }
            >
              {sortedPosts.map((post: any, index: number) => {
                return (
                  <div data-qat={`post_${post.id}`} key={`${post.id}`}>
                    {renderItem({ item: post, index })}
                    <div style={{ backgroundColor: 'rgb(237, 239, 241)', height: 1 }} />
                  </div>
                )
              })}
            </InfiniteScroll>
          ) : (
            !talkStore.isLoading && (
              <View style={{ flex: 1, backgroundColor: color.white, justifyContent: 'flex-start' }}>
                <View style={NULL}>
                  <TalkNull />
                  <Text
                    variant="labelEmphasized"
                    style={{ marginTop: 24, color: 'rgba(38, 38, 38, 1)', marginBottom: 16 }}
                    tx="Discuss.welcomeToFeed"
                  />
                  <Text
                    variant="body1"
                    style={{ color: 'rgba(38, 38, 38, 1)', marginBottom: 16, textAlign: 'center' }}
                    tx="Discuss.conversationReminder"
                  />
                  <Text
                    variant="body1"
                    style={{ color: 'rgba(38, 38, 38, 1)', textAlign: 'center' }}
                    tx="Discuss.respectReminder"
                  />
                </View>
              </View>
            )
          )}
        </div>
      </View>
      {showPostDialog && (
        <CreatePostDialog
          selectedTopicId={params?.topicId}
          onClose={() => {
            setShowPostDialog(false)
          }}
          open={showPostDialog}
        />
      )}
    </>
  )
})

const FULL: ViewStyle = { flex: 1, height: '100%', maxWidth: 800 }
const CONTAINER: React.CSSProperties = {
  height: '100%',
  backgroundColor: color.white,
}
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}
const HEADER: TextStyle = { fontSize: 24, color: color.black }
const SUBHEADER: ViewStyle = {
  flexDirection: 'row',
  paddingBottom: 12,
}
const NULL: ViewStyle = {
  alignItems: 'center',
  flex: 1,
  backgroundColor: '#fff',
  borderRadius: 16,
  margin: 20,
  maxHeight: 355,
  flexDirection: 'column',
  padding: 20,
  paddingTop: 40,
  paddingBottom: 40,
}
const LOADER: React.CSSProperties = {
  height: 80,
  flexDirection: 'column',
  justifyContent: 'center',
}
const PROGRESS: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'center',
}
